import classnames from 'classnames'

import { time } from 'utils'
import { useActions, useStore } from 'components/contexts/event-page'
import { generateQtyOptions } from 'components/contexts/event-page-filters/utils'
import AfterpayMessage from 'components/event/afterpay-message'
import useUser from 'hooks/useUser'
import {
  useActions as useFiltersActions,
  useStore as useFiltersStore,
} from 'components/contexts/event-page-filters'
import { FC } from 'react'
import { useAddToCartMutation } from 'gql/cart'
import { GetEventAuctionByIdQuery } from 'gql'
import { useQueryParams, useDataLayer, useSignUpAsGuest } from '~hooks'
import { Dropdown, Markdown, CartWidget } from '~shared'
import { track } from '~services/fbq'
import { Flex, Typography, Icon, Alert, Button, Grid } from '~ui'

import { USER_BASE_DATA } from 'gql/user/queries'
import styles from './Details.module.scss'
import { PARTNER_SLUG } from '../../../../constants/constant'

const Details: FC<{ event: GetEventAuctionByIdQuery['findArtwork'] }> = ({ event }) => {
  const { onClose, setMessages } = useActions()
  const {
    title,
    checkoutPayload,
    messages,
    price,
    unformatedPrice,
    deliveryNotes,
    longDescription,
    publicNotes,
  } = useStore()

  const { setQuantity } = useFiltersActions()
  const { formQuantity, formQuantityOptions, vip } = useFiltersStore()
  const [signUpAsGuest] = useSignUpAsGuest()

  const { push, paths } = useQueryParams()

  const dl = useDataLayer()

  const { mutate: createCart, loading } = useAddToCartMutation()
  const [user] = useUser()

  const checkoutDisabled = !['shown', 'postponed', 'sales_not_started'].includes(
    event.eventExtendedStatus
  )

  const onCheckout = async () => {
    const checkoutMutationPayload = { ...checkoutPayload, quantity: formQuantity }

    if (!user) {
      const signUpAsGuestResult = await signUpAsGuest({
        variables: { input: { partnerSlug: PARTNER_SLUG } },
        refetchQueries: [USER_BASE_DATA],
      })

      if (signUpAsGuestResult.data.signupGuest.__typename === 'SignupFailure') {
        return
      }
    }

    const createCartResult = await createCart(checkoutMutationPayload)

    if (createCartResult.data.createCart.errorMessage) {
      setMessages([{ message: createCartResult.data.createCart.errorMessage }])
      return
    }

    // Facebook Pixel
    track('AddToCart', checkoutMutationPayload)
    // GA
    dl.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: unformatedPrice,
        item_list_name: checkoutPayload.packageId ? 'packages' : 'tickets',
        items: [
          {
            item_name: title,
            quantity: formQuantity,
            item_id: checkoutPayload.packageId || checkoutPayload.tevoTicketGroupId,
            star_name: event.mainArtist?.fullName,
            event_date: event.eventStartDateLocal,
            tour_name: event.tour?.title,
            venue_name: event.venue?.name,
            event_name: event.title,
          },
        ],
      },
    })

    push(paths.CHECKOUT)
  }

  return (
    <div className={classnames(styles.details)}>
      <button type="button" onClick={onClose} className={styles.close}>
        <Icon name="left-arrow" width="1em" height="1em" fill="currentColor" />
      </button>
      <Flex justifyContent="space-between" alignItems="start" height="auto">
        <Flex flexDirection="column">
          <h3 className={styles.title}>{title}</h3>
          <Typography.Text>{price}</Typography.Text>
        </Flex>
        <Flex flexDirection="column">
          <Dropdown
            options={generateQtyOptions(formQuantityOptions, { type: vip ? 'package' : 'ticket' })}
            onSelect={setQuantity}
            value={formQuantity}
          />
        </Flex>
      </Flex>
      {!!messages?.length && (
        <>
          {messages.map(({ message }) => (
            <Alert mode="info" key={message}>
              {message}
            </Alert>
          ))}
        </>
      )}

      {longDescription && <Markdown md={longDescription} />}

      {event.eventExtendedStatus === 'started' && (
        <Flex>
          <Alert mode="info">
            {`Event occured on ${time.formatDate(event.eventStartDateLocal, 'long')}`}
          </Alert>
        </Flex>
      )}

      <Grid gap={40}>
        <CartWidget>
          {({ cartLoading, isCart }) => (
            <Button
              type="submit"
              variant="primary"
              bodyClr="green"
              clr="green"
              fontClr="black"
              onClick={onCheckout}
              loading={cartLoading || loading}
              disabled={checkoutDisabled || isCart}
              className="btn btn-g-green"
            >
              Checkout
            </Button>
          )}
        </CartWidget>
      </Grid>

      <AfterpayMessage center />

      {deliveryNotes && (
        <div>
          <div className="font-bold flex items-center text-lg lg mb-2">
            <Icon className="block" fill="currentColor" name="tickets" height="1em" width="1em" />
            <span className="ml-2">Delivery notes</span>
          </div>
          <Markdown variant="standart" md={deliveryNotes} />
        </div>
      )}

      {publicNotes && (
        <div>
          <div className="font-bold flex items-center text-lg mb-2">Public notes</div>
          <p>{publicNotes}</p>
        </div>
      )}
    </div>
  )
}

export default Details
